import React, { useState, useEffect, useCallback } from 'react'; 
import { Modal, Button, Card, Alert, Descriptions, Typography, Space } from 'antd';
import type { ButtonProps } from 'antd';
import { useAvevaService } from '@services/useAvevaService';

const { Title, Text } = Typography; // Destructuring: Extracting Title and Text components from the Typography object for easier access.

// Stream IDs: Unique identifiers for the data points in the PI System/Aveva.
const STREAM_LRM = 'PI_pisys.nspower.ca_54688'; // Constant: ID for Lower Regulating Margin stream.
const STREAM_ACE = 'PI_pisys.nspower.ca_54702'; // Constant: ID for Area Control Error stream.

// Thresholds: Business logic limits for triggering alerts.
const THRESHOLD_LRM = 0; // Constant: LRM must be exactly 0.
const THRESHOLD_ACE = 15; // Constant: ACE must be greater than 15.
const DURATION_MINUTES = 5; // Constant: The condition must persist for 5 minutes.

const AvevaAlerts = () => { // Functional Component: The main container for the Alerts feature.
  const avevaApi = useAvevaService(); // Hook Usage: Instantiating the API service to make requests.
  
  // State: UI Controls
  const [loading, setLoading] = useState(false); // State: Boolean to track if an async operation (API call) is in progress.
  const [lastChecked, setLastChecked] = useState<string | null>(null); // State: Stores the timestamp string of the last successful check.
  const [statusMessage, setStatusMessage] = useState<string>(''); // State: Stores the text message displayed to the user in the main panel.
  const [statusType, setStatusType] = useState<'info' | 'warning' | 'error' | 'success'>('info'); // State: Controls the color/styling of the status alert (blue, yellow, red, green).

  // State: Alert Modal Logic
  const [isAlertOpen, setIsAlertOpen] = useState(false); // State: Boolean to control visibility of the pop-up modal.
  const [alertData, setAlertData] = useState<{lrm: number, ace: number, time: string} | null>(null); // State: Object to hold the specific data snapshot when an alert is triggered.

  // Logic: The core function to check alert conditions.
  // useCallback: Memoizes this function so it doesn't change on every render unless 'avevaApi' changes.
  const checkCurtailmentAlert = useCallback(async () => {
    setLoading(true); // Action: Start the loading spinner/state.
    setStatusMessage('Checking conditions...'); // Action: Update text to inform user work is happening.
    setStatusType('info'); // Action: Set status color to neutral blue.

    try {
      // Step 1: Check Lower Regulating Margin (LRM)
      const lrmResponse = await avevaApi.getLatestValue(STREAM_LRM); // API Call: Fetch the single most recent value for LRM.
      const lrmData = lrmResponse.data; // Data Extraction: Get the payload.
      const lrmValue = lrmData?.Value || 0; // Data Normalization: Handle different API response shapes (Value) and default to 0.

      // Condition Check: Is LRM exactly 0?
      if (lrmValue !== THRESHOLD_LRM) {
        // Path: Condition failed. LRM is not 0.
        setStatusMessage(`Condition Not Met: LRM is ${lrmValue} (Expected ${THRESHOLD_LRM})`); // Update status text.
        setStatusType('info'); // Keep status neutral.
        setLoading(false); // Stop loading.
        setLastChecked(new Date().toLocaleTimeString()); // Update timestamp.
        return; // Early Exit: Do not proceed to check ACE.
      }

      // Step 2: Check ACE history (Only runs if Step 1 passed)
      const endTime = new Date(); // Calculation: Current time.
      const startTime = new Date(endTime.getTime() - DURATION_MINUTES * 60 * 1000); // Calculation: 5 minutes ago (Duration * 60s * 1000ms).

      // API Call: Get historical data for ACE over the calculated time range.
      const aceHistoryResponse = await avevaApi.getStreamData(STREAM_ACE, {
        startIndex: startTime.toISOString(), // Start of window.
        endIndex: endTime.toISOString(), // End of window.
        count: 1000 // Parameter: Request enough data points to be safe.
      });

      const aceDataPoints = aceHistoryResponse.data || []; // Data Extraction: Get the array of points or default to empty.

      // Validation: Did we get any data?
      if (aceDataPoints.length === 0) {
        setStatusMessage('Warning: No data found for ACE stream.'); // User Feedback: Warn about missing data.
        setStatusType('warning'); // Visual Feedback: Yellow alert.
        setLoading(false); // Stop loading.
        return; // Early Exit.
      }

      // Data Extraction: Get the last known value for display in the modal.
      const latestAce = aceDataPoints[aceDataPoints.length - 1]?.Value;

      // Logic: Determine if *every* point in the history is above the threshold.
      const isPersistentHigh = aceDataPoints.every((point: any) => { // Array Method: .every() returns true only if callback is true for ALL elements.
        const val = point.Value;
        return typeof val === 'number' && val > THRESHOLD_ACE; // Condition: Value is a number AND > 15.
      });

      if (isPersistentHigh) {
        // Path: Critical Alert Triggered.
        setAlertData({
          lrm: lrmValue,
          ace: latestAce,
          time: new Date().toISOString() // Store current time of alert.
        });
        setIsAlertOpen(true); // Action: Open the Modal.
        setStatusMessage('ALERT TRIGGERED: Curtailment Needed.'); // Update on-page status.
        setStatusType('error'); // Visual Feedback: Red alert.
      } else {
        // Path: Condition failed. ACE was not consistently high.
        setStatusMessage(`Condition Not Met: ACE not consistently > ${THRESHOLD_ACE} for ${DURATION_MINUTES} mins.`); // Explanatory message.
        setStatusType('info'); // Neutral status.
      }

    } catch (error: any) {
      // Error Handling: Catch network errors or code exceptions.
      // eslint-disable-next-line no-console
      console.error('Alert check failed:', error); // Logging: Log to console for debugging.
      setStatusMessage(`Error: ${error.message || 'Check failed'}`); // User Feedback: Show error message.
      setStatusType('error'); // Visual Feedback: Red alert.
    } finally {
      // Cleanup: Runs whether try succeeded or failed.
      setLoading(false); // Ensure loading state is cleared.
      setLastChecked(new Date().toLocaleTimeString()); // Update the "Last Checked" timestamp.
    }
  }, [avevaApi]); // Dependency Array: Re-create function only if 'avevaApi' changes.

  // Effect: Lifecycle management.
  useEffect(() => {
    checkCurtailmentAlert(); // Action: Run the check immediately when component mounts.
    const intervalId = setInterval(checkCurtailmentAlert, 60000); // Action: Set up a timer to run check every 60 seconds.
    return () => clearInterval(intervalId); // Cleanup: Remove the timer when component unmounts.
  }, [checkCurtailmentAlert]); // Dependency: Re-run effect if 'checkCurtailmentAlert' changes.

  return (
    <div style={{ padding: '20px' }} data-testid="aveva-alerts"> 
      <Space direction="vertical" size="large" style={{ width: '100%' }}> 
        {/* Layout: Main header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>System Alerts</Title> 
          <Button
            type="primary" // Style: Primary brand color.
            onClick={checkCurtailmentAlert} // Interaction: Run check on click.
            loading={loading} // Feedback: Show spinner if loading.
            data-testid="aveva-run-checks-button"
          >
            Run Checks Now
          </Button>
        </div>

        {/* Layout: Info Card */}
        <Card title="Curtailment Trigger" bordered={false} data-testid="aveva-curtailment-card">
            <div style={{ marginBottom: 16 }}>
             <Text>
                {/* Description of the alert logic for the user */}
                Monitors Lower Regulating Margin ({STREAM_LRM}) and Area Control Error ({STREAM_ACE}).
                Alerts if LRM is 0 and ACE &gt; {THRESHOLD_ACE} for {DURATION_MINUTES} minutes.
             </Text>
            </div>

            {/* Component: In-page status banner */}
            <Alert
                message={statusMessage} // Prop: The main text.
                description={lastChecked ? `Last checked: ${lastChecked}` : undefined} // Prop: Subtitle with timestamp.
                type={statusType} // Prop: Visual style (info/warning/error).
                showIcon // Prop: Show status icon.
                data-testid="aveva-alert-status"
                data-type={statusType}
            />
        </Card>
      </Space>

      {/* Component: The Pop-up Modal */}
      <Modal
        title="Curtailment Alert" // Header text of modal.
        open={isAlertOpen} // Prop: Visibility control.
        onOk={() => setIsAlertOpen(false)} // Interaction: Close on OK.
        onCancel={() => setIsAlertOpen(false)} // Interaction: Close on Cancel/X.
        okText="Acknowledge" // Custom button text.
        cancelButtonProps={{ style: { display: 'none' } }} // Config: Hide the Cancel button.
        centered // Style: Center on screen.
        wrapProps={{ 'data-testid': 'aveva-curtailment-modal' }}
        okButtonProps={{ 'data-testid': 'modal-ok-button' } as ButtonProps}
      >
        <Alert
            message="Action Required"
            description="Curtailment is needed."
            type="error" // Style: Red/Error.
            showIcon
            style={{ marginBottom: 20 }}
        />
        <p>
            The Lower Regulating Margin is at zero and the Area Control Error (ACE)
            has exceeded {THRESHOLD_ACE} for over {DURATION_MINUTES} minutes.
        </p>

        {/* Component: Data display table */}
        <Descriptions bordered column={1} size="small" data-testid="aveva-alert-details">
            <Descriptions.Item label="Lower Regulating Margin">
                {alertData?.lrm} 
            </Descriptions.Item>
            <Descriptions.Item label="Current ACE Value">
                {alertData?.ace.toFixed(2)} 
            </Descriptions.Item>
            <Descriptions.Item label="Time">
                {alertData ? new Date(alertData.time).toLocaleTimeString() : ''}
            </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
};

export default AvevaAlerts; 
